import { useState, useRef, useEffect } from "react";
import {
  Plus,
  Trash2,
  Video,
  AudioLines,
  Signal,
  ChevronDown,
  ChevronRight,
  Unlink,
  Pencil,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  useStreamAudioFeeds,
  useCreateStreamAudioFeed,
  useUpdateStreamAudioFeed,
  useDeleteStreamAudioFeed,
  useStreamVideoFeeds,
  useCreateStreamVideoFeed,
  useUpdateStreamVideoFeed,
  useDeleteStreamVideoFeed,
} from "@/hooks/useStreamFeeds";
import {
  useStreamSignals,
  useCreateStreamSignal,
  useUpdateStreamSignal,
  useDeleteStreamSignal,
  useAddSignalAudioFeed,
  useUpdateSignalAudioFeed,
  useRemoveSignalAudioFeed,
} from "@/hooks/useStreamSignals";
import type { StreamAudioFeed, StreamVideoFeed } from "@/hooks/useStreamFeeds";
import type { StreamSignal } from "@/hooks/useStreamSignals";

interface StreamFeedsAndSignalsProps {
  projectId: string;
}

export const StreamFeedsAndSignals = ({
  projectId,
}: StreamFeedsAndSignalsProps) => {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <VideoFeedList projectId={projectId} />
        <AudioFeedList projectId={projectId} />
      </div>
      <SignalList projectId={projectId} />
    </div>
  );
};

// ============ Inline Editable Name ============

const InlineEditableName = ({
  value,
  onSave,
}: {
  value: string;
  onSave: (name: string) => void;
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isEditing) inputRef.current?.focus();
  }, [isEditing]);

  const commit = () => {
    const trimmed = draft.trim();
    if (trimmed && trimmed !== value) onSave(trimmed);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <div className="flex items-center gap-1">
        <Input
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") commit();
            if (e.key === "Escape") {
              setDraft(value);
              setIsEditing(false);
            }
          }}
          onBlur={commit}
          className="h-6 text-sm py-0 px-1.5 w-auto min-w-[80px]"
        />
      </div>
    );
  }

  return (
    <button
      type="button"
      className="text-sm hover:underline cursor-pointer text-left"
      onClick={() => {
        setDraft(value);
        setIsEditing(true);
      }}
    >
      {value}
    </button>
  );
};

// ============ Video Feed List ============

const VideoFeedList = ({ projectId }: { projectId: string }) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editFeed, setEditFeed] = useState<StreamVideoFeed | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: feeds = [], isLoading } = useStreamVideoFeeds(projectId);
  const createFeed = useCreateStreamVideoFeed();
  const updateFeed = useUpdateStreamVideoFeed();
  const deleteFeed = useDeleteStreamVideoFeed();

  const handleCreate = (
    data: { name: string; description: string },
    close: boolean,
  ) => {
    createFeed.mutate(
      {
        projectID: projectId,
        name: data.name,
        description: data.description,
        sortOrder: feeds.length,
      },
      close ? { onSuccess: () => setIsAddOpen(false) } : undefined,
    );
  };

  const handleEdit = (data: { name: string; description: string }) => {
    if (!editFeed) return;
    updateFeed.mutate(
      {
        id: editFeed.id,
        projectID: projectId,
        name: data.name,
        description: data.description,
      },
      { onSuccess: () => setEditFeed(null) },
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium flex items-center gap-1.5">
          <Video className="h-4 w-4" /> Video Feeds
        </h4>
        <Button variant="outline" size="sm" onClick={() => setIsAddOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading...</p>
      ) : feeds.length === 0 ? (
        <Card>
          <CardContent className="py-4 text-center text-sm text-muted-foreground">
            No video feeds defined
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {feeds.map((feed) => (
            <div
              key={feed.id}
              className="flex items-center justify-between text-sm py-1.5 px-3 rounded-md border"
            >
              <div className="min-w-0 flex-1">
                <span className="font-medium">{feed.name}</span>
                {feed.description && (
                  <p className="text-xs text-muted-foreground truncate">
                    {feed.description}
                  </p>
                )}
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground"
                  onClick={() => setEditFeed(feed)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleteId(feed.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <VideoFeedDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        title="Add Video Feed"
        onSubmit={handleCreate}
        isLoading={createFeed.isPending}
      />
      {editFeed && (
        <VideoFeedDialog
          open={true}
          onOpenChange={(open) => !open && setEditFeed(null)}
          title="Edit Video Feed"
          initialName={editFeed.name}
          initialDescription={editFeed.description}
          onSubmit={(data) => handleEdit(data)}
          isLoading={updateFeed.isPending}
          mode="edit"
        />
      )}
      <DeleteConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        label="video feed"
        onConfirm={() => {
          if (deleteId)
            deleteFeed.mutate(
              { id: deleteId, projectId },
              { onSuccess: () => setDeleteId(null) },
            );
        }}
      />
    </div>
  );
};

// ============ Audio Feed List ============

const AudioFeedList = ({ projectId }: { projectId: string }) => {
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [editFeed, setEditFeed] = useState<StreamAudioFeed | null>(null);
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: feeds = [], isLoading } = useStreamAudioFeeds(projectId);
  const createFeed = useCreateStreamAudioFeed();
  const updateFeed = useUpdateStreamAudioFeed();
  const deleteFeed = useDeleteStreamAudioFeed();

  const handleCreate = (name: string, close: boolean) => {
    createFeed.mutate(
      { projectID: projectId, name, sortOrder: feeds.length },
      close ? { onSuccess: () => setIsAddOpen(false) } : undefined,
    );
  };

  const handleEdit = (name: string) => {
    if (!editFeed) return;
    updateFeed.mutate(
      { id: editFeed.id, projectID: projectId, name },
      { onSuccess: () => setEditFeed(null) },
    );
  };

  const handleMove = (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= feeds.length) return;
    const a = feeds[index];
    const b = feeds[swapIndex];
    updateFeed.mutate({
      id: a.id,
      projectID: projectId,
      sortOrder: b.sortOrder,
    });
    updateFeed.mutate({
      id: b.id,
      projectID: projectId,
      sortOrder: a.sortOrder,
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium flex items-center gap-1.5">
          <AudioLines className="h-4 w-4" /> Audio Feeds
        </h4>
        <Button variant="outline" size="sm" onClick={() => setIsAddOpen(true)}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add
        </Button>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading...</p>
      ) : feeds.length === 0 ? (
        <Card>
          <CardContent className="py-4 text-center text-sm text-muted-foreground">
            No audio feeds defined
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-1">
          {feeds.map((feed, index) => (
            <div
              key={feed.id}
              className="flex items-center justify-between text-sm py-1.5 px-3 rounded-md border"
            >
              <div className="flex items-center gap-2">
                <div className="flex flex-col gap-0.5 flex-shrink-0">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 p-0"
                    disabled={index === 0}
                    onClick={() => handleMove(index, "up")}
                  >
                    <ChevronRight className="h-3 w-3 -rotate-90" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-4 w-4 p-0"
                    disabled={index === feeds.length - 1}
                    onClick={() => handleMove(index, "down")}
                  >
                    <ChevronRight className="h-3 w-3 rotate-90" />
                  </Button>
                </div>
                <span>{feed.name}</span>
              </div>
              <div className="flex items-center gap-0.5 flex-shrink-0">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground"
                  onClick={() => setEditFeed(feed)}
                >
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                  onClick={() => setDeleteId(feed.id)}
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <AddNameDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        title="Add Audio Feed"
        placeholder="e.g. International Sound"
        onSubmit={handleCreate}
        isLoading={createFeed.isPending}
      />
      {editFeed && (
        <EditNameDialog
          open={true}
          onOpenChange={(open) => !open && setEditFeed(null)}
          title="Edit Audio Feed"
          initialName={editFeed.name}
          onSubmit={handleEdit}
          isLoading={updateFeed.isPending}
        />
      )}
      <DeleteConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        label="audio feed"
        onConfirm={() => {
          if (deleteId)
            deleteFeed.mutate(
              { id: deleteId, projectId },
              { onSuccess: () => setDeleteId(null) },
            );
        }}
      />
    </div>
  );
};

// ============ Signal List ============

const SignalList = ({ projectId }: { projectId: string }) => {
  const [deleteId, setDeleteId] = useState<string | null>(null);

  const { data: signals = [], isLoading } = useStreamSignals(projectId);
  const { data: videoFeeds = [] } = useStreamVideoFeeds(projectId);
  const { data: audioFeeds = [] } = useStreamAudioFeeds(projectId);
  const createSignal = useCreateStreamSignal();
  const updateSignal = useUpdateStreamSignal();
  const deleteSignal = useDeleteStreamSignal();

  const handleAdd = () => {
    const maxSort = signals.reduce(
      (max, s) => Math.max(max, s.sortOrder ?? 0),
      -1,
    );
    createSignal.mutate({
      projectID: projectId,
      number: signals.length + 1,
      name: "",
      sortOrder: maxSort + 1,
    });
  };

  const handleMove = (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= signals.length) return;
    const a = signals[index];
    const b = signals[swapIndex];
    updateSignal.mutate({
      id: a.id,
      projectID: projectId,
      sortOrder: b.sortOrder,
    });
    updateSignal.mutate({
      id: b.id,
      projectID: projectId,
      sortOrder: a.sortOrder,
    });
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium flex items-center gap-1.5">
          <Signal className="h-4 w-4" /> Signals
        </h4>
        <Button variant="outline" size="sm" onClick={handleAdd}>
          <Plus className="h-3.5 w-3.5 mr-1" /> Add Signal
        </Button>
      </div>

      {isLoading ? (
        <p className="text-xs text-muted-foreground">Loading...</p>
      ) : signals.length === 0 ? (
        <Card>
          <CardContent className="py-4 text-center text-sm text-muted-foreground">
            No signals defined
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {signals.map((signal, index) => (
            <SignalCard
              key={signal.id}
              signal={signal}
              index={index}
              total={signals.length}
              projectId={projectId}
              videoFeeds={videoFeeds}
              audioFeeds={audioFeeds}
              onMove={(dir) => handleMove(index, dir)}
              onDelete={() => setDeleteId(signal.id)}
            />
          ))}
        </div>
      )}

      <DeleteConfirmDialog
        open={!!deleteId}
        onOpenChange={(open) => !open && setDeleteId(null)}
        label="signal"
        onConfirm={() => {
          if (deleteId)
            deleteSignal.mutate(
              { id: deleteId, projectId },
              { onSuccess: () => setDeleteId(null) },
            );
        }}
      />
    </div>
  );
};

// ============ Signal Card ============

function audioPairLabel(index: number) {
  return `${index * 2 + 1}+${index * 2 + 2}`;
}

const SignalCard = ({
  signal,
  index,
  total,
  projectId,
  videoFeeds,
  audioFeeds,
  onMove,
  onDelete,
}: {
  signal: StreamSignal;
  index: number;
  total: number;
  projectId: string;
  videoFeeds: StreamVideoFeed[];
  audioFeeds: StreamAudioFeed[];
  onMove: (direction: "up" | "down") => void;
  onDelete: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const updateSignal = useUpdateStreamSignal();
  const addAudioFeed = useAddSignalAudioFeed();
  const updateAudioFeedLink = useUpdateSignalAudioFeed();
  const removeAudioFeed = useRemoveSignalAudioFeed();

  const linkedAudioFeeds = signal.audioFeedLinks ?? [];
  const linkedAudioFeedIds = new Set(
    linkedAudioFeeds.map((l) => l.audioFeedID),
  );
  const availableAudioFeeds = audioFeeds.filter(
    (af) => !linkedAudioFeedIds.has(af.id),
  );

  const handleLinkAudioFeed = (audioFeedId: string) => {
    addAudioFeed.mutate({
      signalID: signal.id,
      audioFeedID: audioFeedId,
      sortOrder: linkedAudioFeeds.length,
    });
  };

  const handleMoveAudioLink = (index: number, direction: "up" | "down") => {
    const swapIndex = direction === "up" ? index - 1 : index + 1;
    if (swapIndex < 0 || swapIndex >= linkedAudioFeeds.length) return;
    const a = linkedAudioFeeds[index];
    const b = linkedAudioFeeds[swapIndex];
    updateAudioFeedLink.mutate({
      id: a.id,
      signalID: signal.id,
      sortOrder: b.sortOrder,
    });
    updateAudioFeedLink.mutate({
      id: b.id,
      signalID: signal.id,
      sortOrder: a.sortOrder,
    });
  };

  return (
    <Card>
      <Collapsible open={isExpanded} onOpenChange={setIsExpanded}>
        <div className="flex items-center justify-between px-4 py-2.5">
          <div className="flex items-center gap-2 min-w-0 flex-1">
            <div className="flex flex-col gap-0.5 flex-shrink-0">
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0"
                disabled={index === 0}
                onClick={() => onMove("up")}
              >
                <ChevronRight className="h-3 w-3 -rotate-90" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                className="h-4 w-4 p-0"
                disabled={index === total - 1}
                onClick={() => onMove("down")}
              >
                <ChevronRight className="h-3 w-3 rotate-90" />
              </Button>
            </div>
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6 flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronDown className="h-4 w-4" />
                ) : (
                  <ChevronRight className="h-4 w-4" />
                )}
              </Button>
            </CollapsibleTrigger>
            <div className="min-w-0">
              <InlineEditableName
                value={signal.name || "Unnamed signal"}
                onSave={(name) =>
                  updateSignal.mutate({
                    id: signal.id,
                    projectID: projectId,
                    name,
                  })
                }
              />
              {signal.description && (
                <p className="text-xs text-muted-foreground truncate">
                  {signal.description}
                </p>
              )}
            </div>
            {signal.videoFeed && (
              <Badge
                variant="secondary"
                className="text-xs gap-1 font-normal flex-shrink-0"
              >
                <Video className="h-3 w-3" /> {signal.videoFeed.name}
              </Badge>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive flex-shrink-0"
            onClick={onDelete}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>

        <CollapsibleContent>
          <div className="px-4 pb-3 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs">Description</Label>
                <Textarea
                  value={signal.description ?? ""}
                  placeholder="Optional description..."
                  rows={2}
                  className="text-sm"
                  onChange={(e) =>
                    updateSignal.mutate({
                      id: signal.id,
                      projectID: projectId,
                      description: e.target.value,
                    })
                  }
                />
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs">Video Feed</Label>
                <Select
                  value={signal.videoFeedID ?? ""}
                  onValueChange={(v) =>
                    updateSignal.mutate({
                      id: signal.id,
                      projectID: projectId,
                      videoFeedID: v || null,
                    })
                  }
                >
                  <SelectTrigger className="h-8">
                    <SelectValue placeholder="Select video feed..." />
                  </SelectTrigger>
                  <SelectContent>
                    {videoFeeds.map((vf) => (
                      <SelectItem key={vf.id} value={vf.id}>
                        {vf.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground flex items-center gap-1">
                  <AudioLines className="h-3 w-3" /> Audio Feeds
                </span>
                {availableAudioFeeds.length > 0 && (
                  <Select value="" onValueChange={handleLinkAudioFeed}>
                    <SelectTrigger className="h-7 w-40">
                      <span className="text-muted-foreground text-xs">
                        Add audio feed...
                      </span>
                    </SelectTrigger>
                    <SelectContent>
                      {availableAudioFeeds.map((af) => (
                        <SelectItem key={af.id} value={af.id}>
                          {af.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
              {linkedAudioFeeds.length === 0 ? (
                <p className="text-xs text-muted-foreground pl-4">
                  No audio feeds linked
                </p>
              ) : (
                <div className="space-y-1">
                  {linkedAudioFeeds.map((link, linkIndex) => (
                    <div
                      key={link.id}
                      className="flex items-center justify-between text-sm py-1 px-3 rounded-md border"
                    >
                      <div className="flex items-center gap-2">
                        <div className="flex flex-col gap-0.5 flex-shrink-0">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0"
                            disabled={linkIndex === 0}
                            onClick={() => handleMoveAudioLink(linkIndex, "up")}
                          >
                            <ChevronRight className="h-3 w-3 -rotate-90" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-4 w-4 p-0"
                            disabled={linkIndex === linkedAudioFeeds.length - 1}
                            onClick={() =>
                              handleMoveAudioLink(linkIndex, "down")
                            }
                          >
                            <ChevronRight className="h-3 w-3 rotate-90" />
                          </Button>
                        </div>
                        <Badge
                          variant="outline"
                          className="text-xs font-mono font-normal"
                        >
                          {audioPairLabel(linkIndex)}
                        </Badge>
                        <span>{link.audioFeed?.name ?? "Unknown"}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0 text-muted-foreground hover:text-destructive"
                        onClick={() =>
                          removeAudioFeed.mutate({
                            id: link.id,
                            signalID: signal.id,
                          })
                        }
                      >
                        <Unlink className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  );
};

// ============ Shared Dialogs ============

const AddNameDialog = ({
  open,
  onOpenChange,
  title,
  placeholder,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  placeholder: string;
  onSubmit: (name: string, close: boolean) => void;
  isLoading: boolean;
}) => {
  const [name, setName] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = (close: boolean) => {
    if (!name.trim()) return;
    onSubmit(name.trim(), close);
    setName("");
    if (!close) setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(false);
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="feed-name">Name</Label>
            <Input
              ref={inputRef}
              id="feed-name"
              placeholder={placeholder}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={isLoading || !name.trim()}>
              Save & Next
            </Button>
            <Button
              type="button"
              variant="secondary"
              disabled={isLoading || !name.trim()}
              onClick={() => submit(true)}
            >
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setName("");
                onOpenChange(false);
              }}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditNameDialog = ({
  open,
  onOpenChange,
  title,
  initialName,
  onSubmit,
  isLoading,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  initialName: string;
  onSubmit: (name: string) => void;
  isLoading: boolean;
}) => {
  const [name, setName] = useState(initialName);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (name.trim()) onSubmit(name.trim());
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="edit-name">Name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="flex gap-2 pt-2">
            <Button type="submit" disabled={isLoading || !name.trim()}>
              Save
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const VideoFeedDialog = ({
  open,
  onOpenChange,
  title,
  initialName = "",
  initialDescription = "",
  onSubmit,
  isLoading,
  mode = "create",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  initialName?: string;
  initialDescription?: string;
  onSubmit: (
    data: { name: string; description: string },
    close: boolean,
  ) => void;
  isLoading: boolean;
  mode?: "create" | "edit";
}) => {
  const [name, setName] = useState(initialName);
  const [description, setDescription] = useState(initialDescription);
  const inputRef = useRef<HTMLInputElement>(null);

  const submit = (close: boolean) => {
    if (!name.trim()) return;
    onSubmit({ name: name.trim(), description: description.trim() }, close);
    if (mode === "create") {
      setName("");
      setDescription("");
      if (!close) setTimeout(() => inputRef.current?.focus(), 0);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            submit(mode === "edit");
          }}
          className="space-y-4"
        >
          <div className="space-y-2">
            <Label htmlFor="vf-name">Name</Label>
            <Input
              ref={inputRef}
              id="vf-name"
              placeholder="e.g. M/E 1"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="vf-description">Description</Label>
            <Textarea
              id="vf-description"
              placeholder="Optional description..."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
            />
          </div>
          <div className="flex gap-2 pt-2">
            {mode === "create" ? (
              <>
                <Button type="submit" disabled={isLoading || !name.trim()}>
                  Save & Next
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  disabled={isLoading || !name.trim()}
                  onClick={() => submit(true)}
                >
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setName("");
                    setDescription("");
                    onOpenChange(false);
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button type="submit" disabled={isLoading || !name.trim()}>
                  Save
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => onOpenChange(false)}
                >
                  Cancel
                </Button>
              </>
            )}
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DeleteConfirmDialog = ({
  open,
  onOpenChange,
  label,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  label: string;
  onConfirm: () => void;
}) => (
  <AlertDialog open={open} onOpenChange={onOpenChange}>
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>Remove {label}?</AlertDialogTitle>
        <AlertDialogDescription>
          This will permanently remove this {label}.
        </AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancel</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm}>Remove</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);
